import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Share2, ArrowLeft, ThumbsUp, ThumbsDown, MessageSquare, Send, Pin, Loader2, Lock, ArrowRight, Trash2, Clock, X, Shield, Heart, Smile, Zap, MessageSquareQuote, Reply } from 'lucide-react';
import { cn } from '../utils';
import Breadcrumbs from '../components/Breadcrumbs';
import ActiveParticipants from '../components/ActiveParticipants';

import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../api';
import { ContentModerationActions, SoftDeletePlaceholder } from '../components/ModeratorControls';
import ConfirmModal from '../components/ConfirmModal';
import { useDiscussion } from '../hooks/useDiscussion';
import useRateLimit from '../hooks/useRateLimit';

const DiscussionRoom = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const {
    discussion,
    comments,
    setComments,
    reactions,
    isUnlocked,
    isRestricted,
    showPasswordPrompt,
    loading,
    handleReaction,
    handleUnlock,
    isModerator,
    fetchData
  } = useDiscussion(id);

  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isSlowMode, timer, startCooldown } = useRateLimit(`discussion-${id}`, 30);
  const [passwordInput, setPasswordInput] = useState('');
  const [accessError, setAccessError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/discussions/${id}/unlock`, { password: passwordInput });
      handleUnlock();
      setAccessError('');
    } catch (err) {
      setAccessError(err?.response?.data?.message || 'Incorrect password. Please try again.');
    }
  };

  const handlePostComment = async (e) => {
    if (e) e.preventDefault();
    if (!newComment.trim() || isSubmitting || isSlowMode) return;

    setIsSubmitting(true);
    startCooldown();

    try {
      const comment = await api.post('/comments', {
        content: newComment,
        commentable_id: id,
        commentable_type: 'discussion',
        parent_id: replyingTo?.id
      });
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setReplyingTo(null);
      addToast('Reply posted!', 'success');
    } catch (err) {
      console.error('Failed to post comment', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId, data) => {
    try {
      const updated = await api.put(`/comments/${commentId}`, data);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, ...data } : c));
    } catch (err) {
      console.error('Failed to update comment', err);
    }
  };

  const toggleDelete = (commentId, currentStatus) => {
    handleUpdateComment(commentId, { is_deleted: !currentStatus });
  };

  const togglePin = (commentId, currentStatus) => {
    handleUpdateComment(commentId, { is_pinned: !currentStatus });
  };

  const toggleMute = (commentId, currentStatus) => {
    handleUpdateComment(commentId, { is_muted: !currentStatus });
  };

  const handleVoteComment = async (commentId) => {
    try {
      const updated = await api.post(`/comments/${commentId}/vote`);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes: updated.likes, dislikes: updated.dislikes, user_vote: updated.user_vote } : c));
    } catch (err) {
      console.error('Failed to vote on comment', err);
    }
  };

  const handleDownvoteComment = async (commentId) => {
    try {
      const updated = await api.post(`/comments/${commentId}/downvote`);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes: updated.likes, dislikes: updated.dislikes, user_vote: updated.user_vote } : c));
    } catch (err) {
      console.error('Failed to downvote on comment', err);
    }
  };

  const handleVote = async () => {
    try {
      const res = await api.post(`/discussions/${id}/vote`);
      fetchData(); // Refresh to get updated vote counts and user status
    } catch (err) {
      console.error('Failed to vote', err);
    }
  };

  const handleDownvote = async () => {
    try {
      const res = await api.post(`/discussions/${id}/downvote`);
      fetchData(); // Refresh
    } catch (err) {
      console.error('Failed to downvote', err);
    }
  };

  const handleInvite = () => {
    navigator.clipboard.writeText(window.location.href);
    addToast('Link copied to clipboard!', 'share');
  };

  const handleDeleteDiscussion = async () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Discussion?',
      description: 'Are you sure you want to delete this discussion? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/discussions/${id}`);
          setConfirmConfig({ isOpen: false });
          navigate(-1);
        } catch (err) {
          console.error('Failed to delete discussion', err);
        }
      }
    });
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-brand-light text-gray-400">
      <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
      <p className="font-bold uppercase tracking-widest text-xs">Loading Discussion...</p>
    </div>
  );

  if (!discussion) return <div className="flex flex-col items-center justify-center min-h-screen bg-brand-light font-bold text-gray-400">
    <p>Discussion not found.</p>
    <button onClick={() => navigate(-1)} className="mt-4 text-brand-indigo underline">Go Back</button>
  </div>;

  return (
    <div className="min-h-screen bg-brand-light pb-24 relative">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 border-b border-gray-100 bg-white flex items-center justify-between gap-4">
        <Breadcrumbs 
          items={[
            discussion.community_name ? { label: discussion.community_name, path: `/c/${discussion.community_id}`, maxWidth: '150px' } : { label: 'Communities', path: '/communities' },
            { label: discussion.title, maxWidth: '250px' }
          ]} 
        />
        <ActiveParticipants 
          participants={discussion.participants || []} 
          count={discussion.members_count || 0}
          variant="indigo"
        />
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl shadow-brand-indigo/5 overflow-hidden mb-8"
        >
          {discussion.image && (
            <div className="w-full h-64 sm:h-96 relative">
              <img src={discussion.image} alt={discussion.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-brand-indigo text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">Discussion</span>
                  {discussion.is_featured && <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1 shadow-lg">
                    <Zap size={10} className="fill-current" />
                    Featured
                  </span>}
                </div>
                <h1 className="text-2xl sm:text-4xl font-black text-white leading-tight drop-shadow-md">{discussion.title}</h1>
              </div>
            </div>
          )}

          <div className="p-6 sm:p-10">
            {!discussion.image && (
               <div className="mb-8">
                 <div className="flex items-center gap-3 mb-4">
                    <span className="bg-brand-indigo/10 text-brand-indigo text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">Discussion</span>
                    {discussion.is_featured && <span className="bg-yellow-50 text-yellow-600 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1">
                      <Zap size={10} className="fill-current" />
                      Featured
                    </span>}
                  </div>
                  <h1 className="text-3xl sm:text-5xl font-black text-gray-900 leading-tight tracking-tight">{discussion.title}</h1>
               </div>
            )}

            <div className="flex items-center justify-between gap-4 mb-8 pb-8 border-b border-gray-50">
               <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-brand-light shadow-sm">
                    <img src={discussion.author_user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${discussion.author}`} alt="author" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold leading-none mb-1">{discussion.author}</p>
                    <p className="text-gray-400 text-xs font-medium">{new Date(discussion.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
               </div>
               <div className="flex items-center gap-2">
                 <button onClick={handleInvite} className="p-3 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-2xl transition-all">
                    <Share2 size={20} />
                 </button>
                 {isModerator && (
                   <div className="flex items-center gap-2">
                      <button onClick={handleDeleteDiscussion} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all">
                        <Trash2 size={20} />
                      </button>
                   </div>
                 )}
               </div>
            </div>

            <div className="prose prose-slate max-w-none mb-12">
               <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap font-medium">{discussion.content}</p>
            </div>

            <div className="flex items-center gap-4">
               <div className="flex items-center p-1 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm">
                  <button 
                    onClick={handleVote}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all",
                      discussion.user_vote?.type === 'upvote' ? "bg-white text-brand-indigo shadow-md" : "text-gray-500 hover:bg-white/50"
                    )}
                  >
                    <ThumbsUp size={18} className={cn(discussion.user_vote?.type === 'upvote' && "fill-current")} />
                    {discussion.likes || 0}
                  </button>
                  <button 
                    onClick={handleDownvote}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all",
                      discussion.user_vote?.type === 'downvote' ? "bg-white text-red-500 shadow-md" : "text-gray-500 hover:bg-white/50"
                    )}
                  >
                    <ThumbsDown size={18} className={cn(discussion.user_vote?.type === 'downvote' && "fill-current")} />
                    {discussion.dislikes || 0}
                  </button>
               </div>
               <div className="flex items-center gap-2 px-5 py-3.5 bg-gray-50 rounded-2xl border border-gray-100 text-gray-500 font-bold text-sm shadow-sm">
                  <MessageSquare size={18} />
                  {comments.length} Replies
               </div>
            </div>
          </div>
        </motion.div>

        <section id="comments" className="space-y-6">
           <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                 <MessageSquareQuote className="text-brand-indigo" />
                 Community Replies
              </h2>
           </div>

           <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-brand-indigo/5 p-6 mb-10">
              {replyingTo && (
                <div className="mb-4 flex items-center justify-between px-4 py-3 bg-brand-indigo/5 rounded-xl border border-brand-indigo/10">
                   <div className="flex items-center gap-2 overflow-hidden">
                      <Reply size={14} className="text-brand-indigo" />
                      <p className="text-xs font-bold text-brand-indigo truncate">Replying to <span className="underline">{replyingTo.user?.name || replyingTo.author}</span></p>
                   </div>
                   <button onClick={() => setReplyingTo(null)} className="text-brand-indigo/40 hover:text-brand-indigo">
                      <X size={16} />
                   </button>
                </div>
              )}
              <div className="relative group">
                <textarea 
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={isSlowMode ? `Wait ${timer}s before posting...` : "Write your thoughts..."}
                  disabled={isSubmitting || isSlowMode}
                  className="w-full bg-gray-50 border-2 border-gray-50 rounded-2xl p-5 font-bold text-gray-700 outline-none transition-all focus:bg-white focus:border-brand-indigo/20 focus:shadow-xl focus:shadow-brand-indigo/5 min-h-[120px] resize-none placeholder:text-gray-300"
                />
                <button 
                  onClick={handlePostComment}
                  disabled={!newComment.trim() || isSubmitting || isSlowMode}
                  className={cn(
                    "absolute bottom-4 right-4 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg",
                    !newComment.trim() || isSubmitting || isSlowMode ? "bg-gray-100 text-gray-400" : "bg-brand-indigo text-white hover:bg-brand-indigo/90 shadow-brand-indigo/20 active:scale-95"
                  )}
                >
                  <Send size={14} />
                  Post Reply
                </button>
              </div>
           </div>

           <div className="space-y-6">
             {comments.length === 0 ? (
               <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-gray-200">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-gray-300">
                     <MessageSquare size={32} />
                  </div>
                  <p className="font-bold text-gray-400">No replies yet. Be the first to join the talk!</p>
               </div>
             ) : (
               comments.filter(c => !c.parent_id).map((comment) => {
                 const replies = comments.filter(r => String(r.parent_id) === String(comment.id));
                 return (
                  <div key={comment.id} className="space-y-4">
                    {comment.is_deleted ? (
                      <SoftDeletePlaceholder 
                        type="comment" 
                        onRestore={() => toggleDelete(comment.id, comment.is_deleted)}
                        sessionId={id}
                        session={discussion}
                      />
                    ) : (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={cn(
                          "bg-white p-6 rounded-[2rem] border transition-all hover:shadow-xl hover:shadow-brand-indigo/5 group",
                          comment.is_pinned ? "border-brand-indigo/30 bg-brand-indigo/[0.02] shadow-lg shadow-brand-indigo/5" : "border-gray-50"
                        )}
                      >
                        <div className="flex items-start justify-between mb-4">
                           <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl overflow-hidden border border-gray-100 shadow-sm">
                                <img src={comment.user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.user?.name || 'guest'}`} alt="avatar" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                  <p className={cn("text-sm font-black text-gray-900", comment.is_muted && "text-gray-400 line-through")}>{comment.user?.name || 'Anonymous'}</p>
                                  {comment.is_pinned && <span className="bg-brand-indigo/10 text-brand-indigo text-[8px] font-bold px-1.5 py-0.5 rounded-lg border border-brand-indigo/10 uppercase tracking-widest flex items-center gap-1">
                                    <Pin size={8} className="fill-current" />
                                    Pinned
                                  </span>}
                                  {String(comment.user_id) === String(discussion.author_id) && <span className="bg-brand-indigo text-white text-[8px] font-bold px-1.5 py-0.5 rounded-lg uppercase tracking-widest">Author</span>}
                                </div>
                                <p className="text-[10px] text-gray-400 font-bold">{new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                           </div>
                           <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <ContentModerationActions 
                                item={comment}
                                type="comment"
                                onPin={() => togglePin(comment.id, comment.is_pinned)}
                                onMute={() => toggleMute(comment.id, comment.is_muted)}
                                onDelete={() => toggleDelete(comment.id, comment.is_deleted)}
                                isModerator={isModerator}
                              />
                           </div>
                        </div>

                        <p className={cn("text-gray-700 font-medium leading-relaxed mb-6 pl-14", comment.is_muted && "opacity-50 select-none")}>
                          {comment.is_muted ? "This reply has been muted by moderators." : comment.content}
                        </p>

                        <div className="flex items-center gap-3 pl-14">
                           <div className="flex items-center bg-gray-50 p-0.5 rounded-xl border border-gray-100 shadow-sm">
                              <button 
                                onClick={() => handleVoteComment(comment.id)}
                                className={cn(
                                  "p-1.5 px-3 rounded-lg transition-all flex items-center gap-1.5 text-xs font-bold",
                                  comment.user_vote?.type === 'upvote' ? "bg-white text-brand-indigo shadow-sm" : "text-gray-400 hover:text-gray-600"
                                )}
                              >
                                <ThumbsUp size={14} className={cn(comment.user_vote?.type === 'upvote' && "fill-current")} />
                                {comment.likes || 0}
                              </button>
                              <button 
                                onClick={() => handleDownvoteComment(comment.id)}
                                className={cn(
                                  "p-1.5 px-3 rounded-lg transition-all flex items-center gap-1.5 text-xs font-bold",
                                  comment.user_vote?.type === 'downvote' ? "bg-white text-red-500 shadow-sm" : "text-gray-400 hover:text-gray-600"
                                )}
                              >
                                <ThumbsDown size={14} className={cn(comment.user_vote?.type === 'downvote' && "fill-current")} />
                                {comment.dislikes || 0}
                              </button>
                           </div>
                           <button 
                             onClick={() => {
                               setReplyingTo(comment);
                               document.querySelector('textarea')?.focus();
                             }}
                             className="text-xs font-bold text-gray-400 hover:text-brand-indigo flex items-center gap-1.5 transition-colors"
                           >
                             <Reply size={14} />
                             Reply
                           </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Nested Replies */}
                    {replies.length > 0 && (
                      <div className="pl-14 space-y-3">
                         {replies.map(reply => (
                           <motion.div 
                              key={reply.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-white/50 p-4 rounded-2xl border border-gray-50"
                           >
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-6 h-6 rounded-lg overflow-hidden border border-gray-100">
                                  <img src={reply.user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${reply.user?.name || 'guest'}`} alt="avatar" />
                                </div>
                                <p className="text-xs font-black text-gray-900">{reply.user?.name || 'Anonymous'}</p>
                                <p className="text-[10px] text-gray-400 font-bold ml-auto">{new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                              <p className="text-xs font-medium text-gray-600 leading-relaxed pl-9">
                                {reply.content}
                              </p>
                           </motion.div>
                         ))}
                      </div>
                    )}
                  </div>
                 )
               })
             )}
           </div>
        </section>
      </main>

      {/* Realtime Reactions Overlay - Matches Room.js */}
      <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
        <AnimatePresence>
          {reactions.map((r) => (
            <motion.div
              key={r.id}
              initial={{ y: '100vh', x: `${r.x}vw`, opacity: 0, scale: 0.5 }}
              animate={{ y: '-10vh', opacity: [0, 1, 1, 0], scale: [0.5, 1.5, 1.5, 1] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.5, ease: 'easeOut' }}
              className="absolute bottom-0 text-4xl sm:text-6xl select-none"
            >
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Floating Reaction Bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 p-2 bg-white/80 backdrop-blur-xl border border-white shadow-2xl rounded-3xl">
         <div className="flex items-center gap-1 border-r border-gray-100 pr-1">
            {[
              { icon: Heart, color: 'text-red-500', emoji: '❤️' },
              { icon: ThumbsUp, color: 'text-blue-500', emoji: '👍' },
              { icon: Smile, color: 'text-yellow-500', emoji: '😊' },
              { icon: Zap, color: 'text-orange-500', emoji: '🔥' },
            ].map((r) => (
              <button
                key={r.emoji}
                onClick={() => handleReaction(r.emoji)}
                className={cn(
                  "p-2.5 rounded-2xl transition-all hover:scale-125 active:scale-90",
                  r.color,
                  "hover:bg-white"
                )}
              >
                <r.icon size={20} className="fill-current opacity-20 group-hover:opacity-100" />
              </button>
            ))}
         </div>
         <button 
           onClick={() => {
              const el = document.getElementById('comments');
              el?.scrollIntoView({ behavior: 'smooth' });
           }}
           className="px-5 py-2.5 text-xs font-black text-gray-900 uppercase tracking-widest flex items-center gap-2 hover:bg-gray-50 rounded-2xl transition-all"
         >
           <MessageSquare size={16} />
           Join Talk
         </button>
      </div>

      <AnimatePresence>
        {isRestricted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Restricted Topic</h2>
              <p className="text-gray-500 font-medium mb-8 leading-relaxed">
                This discussion is invite-only and restricted to specific participants.
              </p>
              
              <button 
                onClick={() => navigate(-1)}
                className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} />
                Go Back
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPasswordPrompt && !isUnlocked && !isRestricted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-brand-indigo/10 text-brand-indigo rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Private Topic</h2>
              <p className="text-gray-500 font-medium mb-8">This discussion is password protected. Please enter the access code to join.</p>
              
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter Access Code"
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-2 border-gray-100 outline-none focus:border-brand-indigo/20 focus:bg-white font-bold text-center transition-all"
                  />
                  {accessError && <p className="mt-2 text-xs font-bold text-red-500">{accessError}</p>}
                </div>
                <div className="flex gap-3">
                  <button 
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex-1 bg-gray-100 text-gray-600 font-bold py-4 rounded-2xl hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="flex-2 bg-brand-indigo text-white font-bold py-4 px-8 rounded-2xl shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all"
                  >
                    Unlock Discussion
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Reactions Canvas */}
      <div className="fixed inset-0 pointer-events-none z-50">
        <AnimatePresence>
          {Array.isArray(reactions) && reactions.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: '80vh', x: `${r.x}vw` }}
              animate={{ opacity: 1, y: '20vh' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="absolute text-3xl"
            >
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Reaction Bar (Floating) */}
      <div className="fixed bottom-24 right-4 md:right-8 z-40 flex flex-col items-center gap-2">
        <div className="bg-white border border-gray-100 rounded-3xl p-1.5 md:p-2 shadow-2xl flex flex-col gap-1.5 md:gap-2">
          {['🔥', '❤️', '👏', '🧠', '😮'].map((emoji) => (
            <button 
              key={emoji}
              onClick={() => handleReaction(emoji)}
              className="p-2.5 md:p-3 text-lg md:text-xl hover:bg-gray-50 rounded-2xl transition-all active:scale-125"
            >
              {emoji}
            </button>
          ))}
        </div>
        <p className="text-[8px] md:text-xxs font-bold text-center text-gray-400 uppercase tracking-widest">React</p>
      </div>

      <ConfirmModal 
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        description={confirmConfig.description}
        onConfirm={confirmConfig.onConfirm}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default DiscussionRoom;
